import React, { useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { formatMoney } from '../../util/currency';
import { formatPossessiveCategory } from '../../util/string';
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Button,
  H2,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';

import css from './ListingPage.module.css';
import SectionPublicData from './SectionPublicData.js';
import VideoPanel from './VideoPanel.js';
import CommentSection from '../../components/CommentSection/CommentSection.js';
import classNames from 'classnames';
import imageOne from '../../../src/assets/one.png';
import imageTwo from '../../../src/assets/two.png';
import imageThree from '../../../src/assets/three.png';
import { KEY_MEASUREMENTS } from '../EditListingPage/EditListingWizard/EditListingMeasurementPanel/EditListingMeasurementPanel.js';
import { categoryOptions, conditions, sleevesOptions } from '../../config/configListing.js';

import W1 from '../../assets/Photos/W1.jpg';
import W2 from '../../assets/Photos/W2.jpg';
import W3 from '../../assets/Photos/W3.jpg';
import W4 from '../../assets/Photos/W4.jpg';
import W5 from '../../assets/Photos/W5.jpg';

import M1 from '../../assets/Photos/M1.jpg';
import M2 from '../../assets/Photos/M2.jpg';
import M3 from '../../assets/Photos/M3.jpg';
import K1 from '../../assets/Photos/K1.jpg';
import K2 from '../../assets/Photos/K2.jpg';
const images = {
  W1: W1,
  W2: W2,
  W3: W3,
  W4: W4,
  W5: W5,
  M1: M1,
  M2: M2,
  M3: M3,
  K1: K1,
  K2: K2,
};
const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
  } = props;
  const commentName = currentUser?.attributes.profile.displayName;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
  console.log(currentListing.attributes, 'currentListing.attributes');

  const { category, subCategory, type } = publicData || {};
  const condition = publicData?.condition;
  const conditionLabel = condition ? conditions?.find(i => i.key == condition)?.label : null;
  const conditionSection = conditionLabel ? conditionLabel : null;

  const data = categoryOptions?.find(cat => cat.key === category);
  const categoryLabel = data?.label;
  const details = formatPossessiveCategory(categoryLabel);
  const subCategories = data?.subCategories || [];
  const subCategoryData = subCategories?.find(item => item.key === subCategory);
  const subCategoryLabel = subCategoryData?.label
    ? subCategoryData?.sLabel || subCategoryData?.label
    : null;
  const typeOptions = subCategoryData && subCategoryData?.type ? subCategoryData?.type : [];
 
  const typeLabel = typeOptions.find(i => i.key == type);

  const sizeOptions = subCategoryData && subCategoryData?.size ? subCategoryData?.size : [];
  const colourOptions = subCategoryData && subCategoryData?.colour ? subCategoryData?.colour : [];
  const selectedColor =
    Array?.isArray(publicData?.colour) && colourOptions?.length
      ? publicData?.colour
          ?.map(i => {
            return colourOptions?.find(j => j.key == i);
          })
          ?.filter(i => i != undefined)
      : [];
  const colorLabels =
    Array.isArray(publicData?.colour) && publicData?.colour?.length
      ? selectedColor
          ?.map(i => {
            if (i.key != 'other') {
              return i?.label;
            } else {
              return publicData?.otherColour;
            }
          })
          ?.join('/')
      : '';
  const typeLabelIfOther = typeLabel?.value == 'other' ? publicData?.otherType : null;
  const sleeveTitle =
    typeLabel?.showSleeves && publicData?.sleeves
      ? sleevesOptions?.find(i => i.key == publicData?.sleeves)?.title
      : null;
  const embroideryOptions =
    subCategoryData && subCategoryData?.embroidery ? subCategoryData?.embroidery : [];
  const embroiderySelected =
    Array.isArray(publicData?.embroidery) &&
    embroideryOptions?.length &&
    publicData?.embroidery
      ?.map(i => {
        return embroideryOptions?.find(j => j.key == i);
      })
      ?.filter(i => i != undefined);
  const embroideryLabel =
    Array.isArray(publicData?.embroidery) &&
    publicData?.embroidery?.length &&
    embroiderySelected?.length
      ? embroiderySelected
          ?.map(i => {
            if (i.key != 'other') {
              return i?.label;
            } else {
              return publicData?.otherEmbroidery;
            }
          })
          ?.join('/')
      : '';
  const fabricsOptionsConfig =
    subCategoryData && subCategoryData?.fabrics ? subCategoryData?.fabrics : [];
  const selectedFabrics =
    Array.isArray(publicData?.fabrics) && fabricsOptionsConfig?.length
      ? publicData?.fabrics
          ?.map(i => {
            return fabricsOptionsConfig?.find(j => j.key == i);
          })
          ?.filter(i => i != undefined)
      : [];
  const fabricsLabel =
    fabricsOptionsConfig?.length && selectedFabrics?.length
      ? selectedFabrics
          ?.map(i => {
            if (i.key != 'other') {
              return i?.label;
            } else {
              return publicData?.otherFabrics;
            }
          })
          ?.join('/')
      : '';
  const desForReview = [
    // conditionLabel,
    // details,
    // colorLabels,
    // sleeveTitle,
    // typeLabelIfOther || (typeLabel?.sLabel || typeLabel?.label || subCategoryLabel),
    fabricsLabel ? fabricsLabel + '.' : '',
    embroideryLabel ? embroideryLabel + '.' : '',
  ]
    .filter(Boolean) // Remove falsy values
    .join(' '); // Join with a single space
  const selectedSize = sizeOptions.find(i => i.key == publicData.size);
  const typeImage = typeOptions.find(i => i.key == type)?.image || [];
  // Fix duplicate possessive (e.g., "Kid's's" -> "Kid's")
  const cleanTitle = title?.replace(/'s's/g, "'s") || title;
  const richTitle = (
    <span>
      {richText(cleanTitle, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;
  const isPublished = currentListing.id && currentListing.attributes.state === 'published';
  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;

  // Product Url, Listing ID, Title, Price, Currency, Availability

  // Product Url, Listing ID, Title, Price, Currency, Availability
  // console.log(productURL)
  // console.log(currentListing.id.uuid)
  // console.log(schemaTitle)
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));
  const classes = classNames(css.root);

  const measurements = {};
  KEY_MEASUREMENTS.forEach(key => {
    measurements[`${key}`] = publicData[`${key}_key`] ? publicData[`${key}_key`] : '00';
  });
  const productDescription = currentListing?.attributes?.description || '';
  const productSize = currentListing?.attributes?.publicData?.size || [];
  const formatSize = size => {
    if (!size) return '';
    if (Array.isArray(size)) {
      return size.join(', ').toUpperCase();
    }
    return size.toString().toUpperCase();
  };

  return (
    <>
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            ...schemaPriceMaybe,
            availability: schemaAvailability,
          },
        }}
      >
        <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
          <div className={css.contentWrapperForProductLayout}>
            <div
              //  onSubmit={handleSubmit}
              className={classes}
            >
              {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                />
              ) : null}
              {currentListing.id ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  isPublished={isPublished}
                  editParams={{
                    id: listingId.uuid,
                    slug: listingSlug,
                    type: listingPathParamType,
                    tab: listingTab,
                  }}
                />
              ) : null}
              {/* BreadCrumbs */}
              <div className={css.breadCrumbs}>
                {category ? (
                  <span
                    className={css.breadCrumb}
                    onClick={() => {
                      history.push(`/s?pub_category=${category}`);
                    }}
                  >
                    <span className={css.breadCrumbLink}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </span>
                  </span>
                ) : null}
                {subCategory ? (
                  <span
                    className={css.breadCrumb}
                    onClick={() => {
                      history.push(`/s?pub_category=${category}&pub_subcategory=${subCategory}`);
                    }}
                  >
                    {' '}
                    {' > '}
                    <span className={css.breadCrumbLink}>
                      {subCategory.charAt(0).toUpperCase() + subCategory.slice(1)}
                    </span>
                  </span>
                ) : null}
                {title ? (
                  <span
                    className={css.breadCrumb}
                    onClick={() => {
                      history.push(`/s?pub_category=${category}&pub_subcategory=${subCategory}`);
                    }}
                  >
                    {' > '}
                    <span className={css.breadCrumbLink}>
                      {title.charAt(0).toUpperCase() + title.slice(1)}
                    </span>
                  </span>
                ) : null}
              </div>
              <div className={css.sectionTwo}>
                <div className={css.images}>
                  <SectionGallery listing={currentListing} variantPrefix={'default'} />
                </div>
                <OrderPanel
                  className={css.productOrderPanel}
                  currentUser={currentUser}
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  onSubmit={handleOrderSubmit}
                  authorLink={
                    <NamedLink
                      className={css.authorNameLink}
                      name="ListingPage"
                      params={params}
                      to={{ hash: '#author' }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  }
                  title={
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  }
                  titleDesktop={
                    <h3 className={css.orderPanelTitle}>
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    </h3>
                  }
                  payoutDetailsWarning={payoutDetailsWarning}
                  author={ensuredAuthor}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onContactUser={onContactUser}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  validListingTypes={config.listing.listingTypes}
                  marketplaceCurrency={config.currency}
                  dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                  marketplaceName={config.marketplaceName}
                  conditionSection={conditionSection}
                  desForReview={desForReview}
                />
                <div className={css.mobileHeading}>
                  <div className={css.detailsContainerMobile}>
                    <h3 className={css.titleMobile}>{title}</h3>
                    <h4 className={css.priceMobile}>{formatMoney(intl, price)}</h4>

                    <p>
                      {desForReview ? (
                        <span className={css.conditionDes}>{desForReview}</span>
                      ) : null}
                      {' ' + productDescription}
                      {conditionSection ? (
                        <span className={css.condition}>
                          <br />
                          Condition:
                          <NamedLink
                            name="FaqPage"
                            className={css.conditionLink}
                            to={{ hash: '#condition' }}
                          >
                            {conditionSection}
                          </NamedLink>
                        </span>
                      ) : null}
                    </p>

                    {productSize && <p>Size: {formatSize(productSize)}</p>}
                  </div>
                </div>
              </div>
              {typeImage?.length ? (
                <div className={css.lastContainer}>
                  <h2 className={css.measurementLabel}>Measurements</h2>
                  <p>
                    Here are the measurements for the item as described by the seller. Please use
                    these to help you determine if the item will fit.
                  </p>

                  {typeImage?.map((data, index) => {
                    const { image, fields = [] } = data || {};
                

                    const imageSrc = images[image];
                    return (
                      <div className={css.measurement}>
                        <div className={css.imageContainer}>
                          <img src={imageSrc} alt="imageOne" className={css.image} />
                        </div>
                        <div className={css.measurementContainer}>
                          <div className={css.measurementDetails}>
                            {fields.map((key, index) => {
                              const dynamicKey = `${key}_key_${image}`;
                              return (
                                <div key={index} className={css.measurementContent}>
                                  <p className={css.measurementKey}>{key}</p>
                                  {/* <p className={css.measurementValue}>"{`${key}_key_${image}` || '00'}"</p> */}
                                  <p className={css.measurementValue}>{publicData[dynamicKey]?`"${publicData[dynamicKey]}"` :'—' }</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {/* <div className={css.lastContainer}>
                <div className={css.imageContainer}>
                  <img src={imageOne} alt="imageOne" className={css.image} />
                  <img src={imageTwo} alt="imageTwo" className={css.image} />
                  <img src={imageThree} alt="imageThree" className={css.image} />
                </div>
                <div className={css.measurementContainer}>
                  <h2 className={css.measurement}>Measurements</h2>
                  <p>Based on these measurements, this item is considered a M-L in Canada.</p>
                  <div className={css.measurementDetails}>
                    {measurements &&
                      Object.keys(measurements).map((key, index) => {
                        return (
                          <div key={index} className={css.measurementContent}>
                            <p className={css.measurementKey}>{key}</p>
                            <p className={css.measurementValue}>{measurements[key]}"</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div> */}
              {/* Comment section goes here. */}
              {isAuthenticated && (
                <CommentSection
                  productId={currentListing.id.uuid}
                  commentUserId={currentUser?.id?.uuid}
                  listingAuthorId={currentListing?.author?.id?.uuid}
                  authorName={
                    currentUser?.attributes?.profile?.publicData?.storeName ||
                    currentUser?.attributes?.profile?.displayName
                  }
                />
              )}
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    </>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
